import { useEffect } from 'react';

import {
  invalidateExtractedTablesQuery,
  useGetExtractedTables,
} from '@frontend/api';
import {
  TaskType,
  PartWorkerTasks,
  TaskStatus,
  TaskResolution,
} from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';

import { useTaskStatusTracking } from 'utils/hooks/taskTracking/useTaskStatusTracking';

/**
 * Handles invalidating extracted tables query by listening for task status updates
 */
export const useLiveTableResults = ({
  partFileUuids,
}: {
  partFileUuids: string[];
}) => {
  const memoizedPartFileUuids = useMemoizedArray(partFileUuids);

  const isBeamsEnabled = useFeatureFlagValue(featureFlagKeys.isBeamsEnabled);

  // Pull all extracted tables for the input file UUIDs
  const { isFetching: isTablesDataFetching, data: extractedTablesData } =
    useGetExtractedTables({
      queryOptions: {
        enabled: memoizedPartFileUuids.length > 0 && isBeamsEnabled,
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
      query: {
        uuids: memoizedPartFileUuids,
      },
    });

  const { registerCallback, unregisterCallback } = useTaskStatusTracking();

  useEffect(() => {
    const callbackKey = `extracted_tables_${memoizedPartFileUuids.join(',')}`;

    const applicableTasks = new Set<TaskType>([
      PartWorkerTasks.CROPPED_TABLE_ANALYSIS,
      PartWorkerTasks.ANALYZE_DOCUMENT,
    ]);

    registerCallback(callbackKey, ({ updateMessages }) => {
      for (const update of updateMessages) {
        if (
          applicableTasks.has(update.task) &&
          memoizedPartFileUuids.includes(update.entityKey) &&
          update.status === TaskStatus.COMPLETED &&
          update.resolution === TaskResolution.SUCCESS
        ) {
          invalidateExtractedTablesQuery({
            partFileUuids: memoizedPartFileUuids,
          });
          break;
        }
      }
    });

    return () => unregisterCallback(callbackKey);
  }, [memoizedPartFileUuids, registerCallback, unregisterCallback]);

  return {
    isTablesDataFetching,
    extractedTablesData,
  };
};
